import React, { useCallback, useState } from 'react';
import {
  Gift,
  Loader2,
  Package,
  Search,
  Smartphone,
  User,
  Wrench,
} from 'lucide-react';
import { fmt } from './posV2Utils';

const PosV2ClientDrawer = ({ api, onSelectClient, onClose }) => {
  const [searchQ, setSearchQ] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [error, setError] = useState('');

  const searchClients = useCallback(async () => {
    const q = searchQ.trim();
    if (q.length < 2) return;
    setSearching(true);
    setError('');
    try {
      const res = await api.get(`/clients/autocomplete/${encodeURIComponent(q)}`);
      setResults(res.data || []);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  }, [api, searchQ]);

  const loadProfile = async (clientId, telephone) => {
    setLoadingProfile(true);
    setError('');
    try {
      const params = clientId ? { client_id: clientId } : { telephone };
      const res = await api.get('/pos/v2/clients/profile', { params });
      setProfile(res.data);
      setResults([]);
    } catch (err) {
      setProfile(null);
      setError(err.response?.data?.detail || 'Client introuvable');
    } finally {
      setLoadingProfile(false);
    }
  };

  const applyToCheckout = () => {
    if (!profile?.client) return;
    onSelectClient({
      nom: profile.client.nom || '',
      telephone: profile.client.telephone || '',
      loyalty: profile.loyalty,
      remise_pct: Number(profile.client.remise_pct || 0),
    });
    onClose();
  };

  const loyalty = profile?.loyalty;

  return (
    <div className="space-y-4 flex flex-col min-h-[60vh]">
      <p className="text-sm text-gray-400 flex items-start gap-2">
        <User size={18} className="text-emerald-400 shrink-0 mt-0.5" />
        <span>Historique client · fidélité · rattacher à l&apos;encaissement</span>
      </p>

      {error && (
        <p className="text-xs text-red-300 bg-red-950/30 border border-red-500/30 rounded-lg px-3 py-2">{error}</p>
      )}

      {!profile && (
        <div className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" size={16} />
              <input
                className="w-full pl-9 pr-3 py-3 rounded-lg bg-black/30 border border-white/10 text-white"
                placeholder="Nom ou téléphone…"
                value={searchQ}
                onChange={(e) => setSearchQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && searchClients()}
              />
            </div>
            <button type="button" onClick={searchClients} className="px-4 py-3 rounded-lg bg-red-600 font-semibold">
              {searching ? <Loader2 size={16} className="animate-spin" /> : 'Chercher'}
            </button>
          </div>
          <div className="space-y-1 max-h-64 overflow-y-auto">
            {results.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => loadProfile(c.id, c.telephone)}
                className="w-full text-left p-3 rounded-lg border border-white/10 bg-black/20 text-gray-200 hover:border-emerald-500/40"
              >
                <div className="font-semibold text-sm">{c.nom}</div>
                <div className="text-xs text-gray-500">{c.telephone}</div>
              </button>
            ))}
            {!results.length && !searching && searchQ.trim().length >= 2 && (
              <p className="text-center text-gray-500 text-sm py-4">Aucun client — créez-le depuis Commandes ou encaissement</p>
            )}
          </div>
        </div>
      )}

      {loadingProfile && (
        <p className="text-center text-gray-500 py-8 flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={20} /> Chargement…
        </p>
      )}

      {profile?.client && !loadingProfile && (
        <div className="space-y-3 flex-1 overflow-y-auto">
          <div className="p-3 rounded-xl border border-white/10 bg-black/30">
            <div className="font-bold text-lg">{profile.client.nom}</div>
            <div className="text-sm text-gray-400">{profile.client.telephone}</div>
            {profile.client.email && <div className="text-xs text-gray-500 mt-1">{profile.client.email}</div>}
            {Number(profile.client.remise_pct || 0) > 0 && (
              <div className="text-xs text-emerald-400 mt-1">Remise client : -{profile.client.remise_pct}%</div>
            )}
            {loyalty && (
              <div className="mt-3 flex flex-wrap gap-2 items-center">
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] bg-amber-900/40 text-amber-200 border border-amber-500/30">
                  <Gift size={12} />
                  {loyalty.level_name || 'Bronze'} · {loyalty.current_points} pts
                </span>
                {loyalty.pending_reward && (
                  <span className="text-[10px] text-emerald-300 font-semibold">Récompense disponible</span>
                )}
              </div>
            )}
          </div>

          <Section icon={Wrench} title="Réparations récentes" empty="Aucune réparation">
            {profile.repairs?.map((r) => (
              <Row key={r.ref} left={r.ref} sub={[r.brand, r.model].filter(Boolean).join(' ')} right={r.status} />
            ))}
          </Section>

          <Section icon={Package} title="Commandes" empty="Aucune commande">
            {profile.commandes?.map((c) => (
              <Row key={c.id} left={c.ref_commande || c.piece?.slice(0, 24)} sub={c.piece} right={`${fmt(c.prix_final)} €`} />
            ))}
          </Section>

          <Section icon={Smartphone} title="Achats / acomptes" empty="Aucun historique">
            {profile.ventes?.slice(0, 6).map((v) => (
              <Row key={v.ref_vente} left={v.designation?.slice(0, 28) || v.ref_vente} sub={v.type} right={`${fmt(v.prix_total)} €`} />
            ))}
          </Section>

          <div className="flex gap-2 pt-2">
            <button type="button" onClick={() => { setProfile(null); setSearchQ(''); }} className="flex-1 py-3 rounded-xl border border-white/10 text-sm text-gray-300">
              Autre client
            </button>
            <button type="button" onClick={applyToCheckout} className="flex-1 py-3 rounded-xl bg-emerald-600 font-bold">
              Utiliser à l&apos;encaissement
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ icon: Icon, title, empty, children }) => {
  const items = React.Children.toArray(children).filter(Boolean);
  return (
    <div className="rounded-lg border border-white/10 bg-black/30 p-2 text-gray-200">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wide text-gray-500 mb-2">
        <Icon size={12} /> {title}
      </div>
      {items.length ? items : <p className="text-xs text-gray-600 py-1">{empty}</p>}
    </div>
  );
};

const Row = ({ left, sub, right }) => (
  <div className="flex justify-between gap-2 py-1.5 border-b border-white/5 last:border-0 text-xs text-gray-200">
    <div className="min-w-0">
      <div className="truncate font-medium">{left}</div>
      {sub && <div className="truncate text-gray-500">{sub}</div>}
    </div>
    {right && <span className="shrink-0 text-gray-400">{right}</span>}
  </div>
);

export default PosV2ClientDrawer;
